import React, { useState, useEffect, useMemo } from 'react';
import { Form, Select, Button, message, Card, Space, Row, Col, Typography, Alert } from 'antd';
import { SettingOutlined, LinkOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import {
    GET_ALL_PRICING_FEE_ITEMS,
    GET_ALL_PRICING_FACTORS,
    GET_PRICING_FEE_ITEM_FACTORS,
    SAVE_FEE_ITEM_FACTORS
} from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import PricingFactorValueCard from './components/factor/PricingFactorValueCard';

const { Title, Text } = Typography;

interface PricingFeeItem {
    id: string;
    businessFeeCode: number;
    businessFeeName: string;
    businessScenarioCode: number;
    businessCode: number;
    description?: string;
}

interface PricingFactor {
    code: string;
    key: string;
    description: string;
}

interface PricingFeeItemFactor {
    id: string;
    feeItemCode: number;
    pricingFactor: string;
}

const FeeItemFactorConfigPage: React.FC = () => {
    const [form] = Form.useForm();
    const [selectedFeeItemCode, setSelectedFeeItemCode] = useState<number | undefined>(undefined);

    // 查询所有费用项
    const { data: feeItemsData, loading: feeItemsLoading } = useQuery(GET_ALL_PRICING_FEE_ITEMS);

    // 查询所有计费因子
    const { data: factorsData, loading: factorsLoading } = useQuery(GET_ALL_PRICING_FACTORS);

    // 查询已配置的计费因子
    const { data: existingFactorsData, loading: existingFactorsLoading, refetch: refetchExistingFactors } = useQuery(
        GET_PRICING_FEE_ITEM_FACTORS,
        {
            variables: { feeItemCode: selectedFeeItemCode! },
            skip: !selectedFeeItemCode,
        }
    );

    // 保存变更
    const [saveFeeItemFactors, { loading: saving }] = useMutation(SAVE_FEE_ITEM_FACTORS);

    // 费用项选项
    const feeItemOptions = useMemo(() => {
        if (!feeItemsData?.getAllPricingFeeItems) return [];
        return feeItemsData.getAllPricingFeeItems.map((item: PricingFeeItem) => ({
            label: `${item.businessFeeName} (${item.businessFeeCode})`,
            value: item.businessFeeCode,
        }));
    }, [feeItemsData]);

    // 计费因子选项
    const factorOptions = useMemo(() => {
        if (!factorsData?.getAllPricingFactors) return [];
        return factorsData.getAllPricingFactors.map((factor: PricingFactor) => ({
            label: `${factor.description} (${factor.code})`,
            value: factor.code,
        }));
    }, [factorsData]);

    // 已配置的计费因子编码列表
    const existingFactorCodes = useMemo(() => {
        if (!existingFactorsData?.getPricingFeeItemFactorsByFeeItemCode) return [];
        return existingFactorsData.getPricingFeeItemFactorsByFeeItemCode
            .map((item: PricingFeeItemFactor) => item.pricingFactor)
            .filter((code: string | null): code is string => code !== null);
    }, [existingFactorsData]);

    // 当选择费用项时，加载已配置的计费因子
    useEffect(() => {
        if (selectedFeeItemCode) {
            refetchExistingFactors();
        }
    }, [selectedFeeItemCode, refetchExistingFactors]);

    // 当已配置的计费因子加载完成后，设置表单值
    useEffect(() => {
        if (selectedFeeItemCode && existingFactorCodes.length > 0) {
            form.setFieldsValue({
                pricingFactors: existingFactorCodes,
            });
        } else if (selectedFeeItemCode) {
            form.setFieldsValue({
                pricingFactors: [],
            });
        }
    }, [existingFactorCodes, selectedFeeItemCode, form]);

    // 处理费用项选择变化
    const handleFeeItemChange = (value: number) => {
        setSelectedFeeItemCode(value);
        form.setFieldsValue({
            pricingFactors: [],
        });
    };

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (!selectedFeeItemCode) {
                message.error('请选择费用项');
                return;
            }

            const result = await saveFeeItemFactors({
                variables: {
                    input: {
                        feeItemCode: selectedFeeItemCode,
                        pricingFactorCodes: values.pricingFactors || [],
                    },
                },
            });

            if (result.data?.saveFeeItemFactors?.success) {
                message.success(result.data.saveFeeItemFactors.message);
                // 清除表单数据
                form.resetFields();
                setSelectedFeeItemCode(undefined);
            } else {
                message.error(result.data?.saveFeeItemFactors?.message || '保存失败');
            }
        } catch (error: any) {
            console.error('保存失败:', error);
            message.error(error.message || '保存失败');
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <div className="mb-2">
                        <Title level={2} style={{ marginBottom: 0 }}>
                            <SettingOutlined className="mr-2" />
                            费用项与计费因子配置
                        </Title>
                    </div>
                    <Text type="secondary">
                        维护计费因子值，并建立费用项与计费因子之间的关联关系
                    </Text>
                </div>

                <Row gutter={[24, 24]}>
                    {/* 计费因子值：列表 + 新增 / 改名称 / 删除 */}
                    <Col xs={24}>
                        <PricingFactorValueCard
                            factorTypeOptions={factorOptions}
                            factorTypesLoading={factorsLoading}
                        />
                    </Col>

                    {/* 费用项与计费因子关系配置 */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <span>
                                    <LinkOutlined className="mr-2" />
                                    费用项与计费因子关联
                                </span>
                            }
                            bordered={false}
                            className="h-full shadow-sm"
                            headStyle={{ borderBottom: '1px solid #f0f0f0' }}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSubmit}
                            >
                                <Form.Item
                                    label={
                                        <span className="font-medium">
                                            费用项
                                        </span>
                                    }
                                    name="feeItem"
                                    rules={[{ required: true, message: '请选择费用项' }]}
                                >
                                    <Select
                                        placeholder="请选择费用项"
                                        style={{ width: '100%' }}
                                        loading={feeItemsLoading}
                                        onChange={handleFeeItemChange}
                                        showSearch
                                        size="large"
                                        filterOption={(input, option) => {
                                            const label = String(option?.label ?? '');
                                            return label.toLowerCase().includes(input.toLowerCase());
                                        }}
                                        options={feeItemOptions}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={
                                        <span className="font-medium">
                                            计费因子
                                            <Text type="secondary" className="ml-2 text-xs">
                                                （可多选）
                                            </Text>
                                        </span>
                                    }
                                    name="pricingFactors"
                                    rules={[{ required: true, message: '请至少选择一个计费因子' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder={
                                            selectedFeeItemCode
                                                ? '请选择计费因子（可多选）'
                                                : '请先选择费用项'
                                        }
                                        style={{ width: '100%' }}
                                        loading={factorsLoading || existingFactorsLoading}
                                        disabled={!selectedFeeItemCode}
                                        size="large"
                                        options={factorOptions}
                                        maxTagCount="responsive"
                                    />
                                </Form.Item>

                                {!selectedFeeItemCode && (
                                    <Alert
                                        message="提示"
                                        description="请先选择费用项，然后为该费用项配置关联的计费因子"
                                        type="info"
                                        showIcon
                                        className="mb-4"
                                    />
                                )}

                                {selectedFeeItemCode && existingFactorCodes.length > 0 && (
                                    <Alert
                                        message="注意"
                                        description="如果是已有的配置，那么保存会将之前的配置覆盖掉！请谨慎操作"
                                        type="warning"
                                        showIcon
                                        className="mb-4"
                                    />
                                )}

                                <Form.Item className="mb-0">
                                    <Space>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={saving}
                                            size="large"
                                            disabled={!selectedFeeItemCode}
                                            icon={<LinkOutlined />}
                                        >
                                            保存关联关系
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                form.resetFields();
                                                setSelectedFeeItemCode(undefined);
                                            }}
                                            size="large"
                                        >
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default FeeItemFactorConfigPage;